import { useEffect, useMemo, useState } from 'react'

import { LocationSubdivisionViews, LocationViews } from '@img-arena/consoles-api-streaming'
import { AnalyticsFiltersParams } from '@img-arena/consoles-api-streaming'
import { StatisticsCsvData } from '@/components/HistoricalStatistics/StatisticsCard/StatisticsCard'
import { Column, Sort } from '@/components/table/base'
import { streamingRestApi } from '@/service/streamingApi'

import { STATISTICS_CSV_HEADERS } from '../../constants'
import { CA_PROVINCES_VIEWS_COLUMNS } from './caProvinces.table'
import { COUNTRIES_VIEWS_COLUMNS } from './countryViews.table'
import { US_STATES_VIEWS_COLUMNS } from './usStates.table'

export enum TabKey {
  COUNTRY = 'country',
  US_STATE = 'usState',
  CA_PROVINCE = 'caProvince'
}

export type Tab = {
  value: TabKey
  label: string
  data: Record<string, unknown>[] | null
  columns: Column<Record<string, unknown>>[]
  csvData: StatisticsCsvData
  defaultSort: Partial<Sort<string>>
}

export const useRegionTabs = (queryParams: AnalyticsFiltersParams) => {
  const analyticsEndpoints = streamingRestApi.endpoints.analytics

  const [countryViews, setCountryViews] = useState<LocationViews['views'] | null>(null)
  const [usStatesViews, setUsStatesViews] = useState<LocationSubdivisionViews['views'] | null>(null)
  const [caProvincesViews, setCaProvincesViews] = useState<
    LocationSubdivisionViews['views'] | null
  >(null)

  const defaultSort = 'totalViews'

  useEffect(() => {
    const ac = new AbortController()

    analyticsEndpoints.getLocationViews({ ...queryParams }, ac.signal).then((data) => {
      setCountryViews(data.views)
    })

    analyticsEndpoints
      .getLocationSubdivisionViews({ ...queryParams, countryCode: 'US' }, ac.signal)
      .then((data) => {
        setUsStatesViews(data.views)
      })

    analyticsEndpoints
      .getLocationSubdivisionViews({ ...queryParams, countryCode: 'CA' }, ac.signal)
      .then((data) => {
        setCaProvincesViews(data.views)
      })

    return () => {
      ac.abort()
    }
  }, [...Object.values(queryParams)])

  return useMemo(
    () =>
      [
        {
          value: TabKey.COUNTRY,
          label: 'Countries',
          data: countryViews,
          columns: COUNTRIES_VIEWS_COLUMNS,
          csvData: {
            csvHeaders: [
              STATISTICS_CSV_HEADERS.LOCATION,
              STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
              STATISTICS_CSV_HEADERS.UNIQUE_VIEWS,
              STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
              STATISTICS_CSV_HEADERS.AVERAGE_DURATION
            ],
            data: countryViews?.map((value) => ({
              [STATISTICS_CSV_HEADERS.LOCATION]: value.location,
              [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: value.totalViews,
              [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: value.uniqueViews,
              [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: value.percentageViews,
              [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: value.averageDuration
            }))
          },
          defaultSort
        },
        {
          value: TabKey.US_STATE,
          label: 'US States',
          data: usStatesViews,
          columns: US_STATES_VIEWS_COLUMNS,
          csvData: {
            csvHeaders: [
              STATISTICS_CSV_HEADERS.LOCATION,
              STATISTICS_CSV_HEADERS.STATE_NAME,
              STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
              STATISTICS_CSV_HEADERS.UNIQUE_VIEWS,
              STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
              STATISTICS_CSV_HEADERS.AVERAGE_DURATION
            ],
            data: usStatesViews?.map((value) => ({
              [STATISTICS_CSV_HEADERS.LOCATION]: value.location,
              [STATISTICS_CSV_HEADERS.STATE_NAME]: value.subdivision,
              [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: value.totalViews,
              [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: value.uniqueViews,
              [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: value.percentageViews,
              [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: value.averageDuration
            }))
          },
          defaultSort
        },
        {
          value: TabKey.CA_PROVINCE,
          label: 'CA Provinces',
          data: caProvincesViews,
          columns: CA_PROVINCES_VIEWS_COLUMNS,
          csvData: {
            csvHeaders: [
              STATISTICS_CSV_HEADERS.LOCATION,
              STATISTICS_CSV_HEADERS.PROVINCE_NAME,
              STATISTICS_CSV_HEADERS.TOTAL_VIEWS,
              STATISTICS_CSV_HEADERS.UNIQUE_VIEWS,
              STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS,
              STATISTICS_CSV_HEADERS.AVERAGE_DURATION
            ],
            data: caProvincesViews?.map((value) => ({
              [STATISTICS_CSV_HEADERS.LOCATION]: value.location,
              [STATISTICS_CSV_HEADERS.PROVINCE_NAME]: value.subdivision,
              [STATISTICS_CSV_HEADERS.TOTAL_VIEWS]: value.totalViews,
              [STATISTICS_CSV_HEADERS.UNIQUE_VIEWS]: value.uniqueViews,
              [STATISTICS_CSV_HEADERS.PERCENTAGE_VIEWS]: value.percentageViews,
              [STATISTICS_CSV_HEADERS.AVERAGE_DURATION]: value.averageDuration
            }))
          },
          defaultSort
        }
      ] as Tab[],
    [countryViews, usStatesViews, caProvincesViews]
  )
}
